/**
 * Top Properties by Savings section: donut chart (Property Savings) + table.
 * Data: summary by_properties_data merged with property list.
 * Donut shows Top 5 + "Other" when there are more than 5 properties.
 */

import { Box } from '@mui/material';
import { differenceInMonths } from 'date-fns';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductSourcingModes } from 'entities/ProductSourcing';
import { toNumber } from 'shared/lib/number/number';
import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
} from 'widgets/ComposableReportWidget';
import {
    buildSavingsRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import {
    augmentItemsForTopNOther,
    buildTopNChartItemsWithOther,
} from '../model/lib/topItemsForChart';

import type { Property } from 'entities/Property/model/types/PropertySchema';
import type { PropertySavingsDto } from 'entities/ReportSavingsSummary';

const PIE_MAX_ITEMS = 5;
const TABLE_INITIAL_LIMIT = 5;

type SavingsSummaryPropertiesTableColumnId =
    | 'property'
    | 'productSourcing'
    | 'units'
    | 'orders'
    | 'totalSaved'
    | 'savedPerUnit'
    | 'savedPerUnitMo'
    | 'totalSpend';

function getProductSourcingLabel(
    mode: string | undefined,
    t: (key: string) => string,
): string {
    if (mode === ProductSourcingModes.AvailabilityPrice) {
        return t('Best Price');
    }

    return mode ? t('Preferred') : '-';
}

function propertyToWidgetItem(
    dto: PropertySavingsDto,
    property: Property | undefined,
    t: (key: string) => string,
    monthsInRange: number,
): ReportWidgetItem {
    const savingsNum = Number.parseFloat(dto.savingsAmount) || 0;
    const unitCount = property?.unitCount ?? null;
    const units = unitCount ?? '';
    const savedPerUnit =
        unitCount != null && unitCount > 0 ? savingsNum / unitCount : '';
    const savedPerUnitMo =
        monthsInRange > 0 && unitCount != null && unitCount > 0
            ? savingsNum / unitCount / monthsInRange
            : '';
    const productSourcing = getProductSourcingLabel(
        property?.sourcingSettings?.mode,
        t,
    );
    const orderCount = dto.orderCount ?? '';
    const totalSpend = dto.totalSpendSum ?? '';

    return {
        id: dto.propertyId,
        label: dto.propertyName?.trim() || dto.propertyId,
        primaryValue: savingsNum,
        secondaryValues: {
            label: dto.propertyName?.trim() || dto.propertyId,
            productSourcing,
            units,
            orderCount,
            totalSaved: dto.savingsAmount,
            savedPerUnit,
            savedPerUnitMo,
            totalSpend,
        },
        meta: { propertyId: dto.propertyId, property },
    };
}

interface SavingsSummaryPropertiesSectionProps {
    byPropertiesData: PropertySavingsDto[];
    properties: Property[];
    loading?: boolean;
    dateFrom: Date;
    dateTo: Date;
    onPropertySelect?: (payload: ReportWidgetInteractionPayload) => void;
    disableChartAnimation?: boolean;
    tableOnly?: boolean;
    subheader?: string;
    tableColumnIds?: SavingsSummaryPropertiesTableColumnId[];
    showBottomBorder?: boolean;
}

export const SavingsSummaryPropertiesSection = memo(
    ({
        byPropertiesData,
        properties,
        loading = false,
        dateFrom,
        dateTo,
        onPropertySelect,
        disableChartAnimation = false,
        tableOnly = false,
        subheader,
        tableColumnIds,
        showBottomBorder = true,
    }: SavingsSummaryPropertiesSectionProps) => {
        const { t } = useTranslation();

        const propertyMap = useMemo(
            () => new Map(properties.map((p) => [p.id, p])),
            [properties],
        );

        const monthsInRange = useMemo(
            () => Math.max(1, differenceInMonths(dateTo, dateFrom) + 1),
            [dateFrom, dateTo],
        );

        const items: ReportWidgetItem[] = useMemo(() => {
            const sorted = [...byPropertiesData].sort((a, b) => {
                const aSavings = Number.parseFloat(a.savingsAmount) || 0;
                const bSavings = Number.parseFloat(b.savingsAmount) || 0;
                return bSavings - aSavings;
            });

            const baseItems = sorted.map((dto) =>
                propertyToWidgetItem(
                    dto,
                    propertyMap.get(dto.propertyId),
                    t,
                    monthsInRange,
                ),
            );

            return augmentItemsForTopNOther({
                items: baseItems,
                topCount: 5,
                otherChartItemId: 'savings-summary-properties-other',
            });
        }, [byPropertiesData, propertyMap, t, monthsInRange]);

        const chartItems: ReportWidgetItem[] | undefined = useMemo(() => {
            if (tableOnly) return undefined;
            if (items.length <= PIE_MAX_ITEMS) return undefined;

            return buildTopNChartItemsWithOther({
                items,
                topCount: 5,
                otherItemId: 'savings-summary-properties-other',
                otherLabel: t('Other'),
            });
        }, [items, t, tableOnly]);

        const handleInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                if (payload.item.meta?.isAggregatedOther) return;
                if (onPropertySelect) {
                    onPropertySelect(payload);
                }
            },
            [onPropertySelect],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<
                SavingsSummaryPropertiesTableColumnId,
                ReportWidgetColumnConfig
            > = {
                property: {
                    id: 'property',
                    label: t('Property'),
                    valueKey: 'label',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    minWidth: 200,
                    maxWidth: 240,
                    allowWrap: true,
                },
                productSourcing: {
                    id: 'productSourcing',
                    label: t('savingsSummary.propertiesColumnProductSourcing'),
                    valueKey: 'productSourcing',
                    format: 'text',
                    align: 'left',
                    headerTooltip: t('savingsSummary.productSourcingTooltip'),
                    minWidth: 120,
                    allowWrap: true,
                },
                units: {
                    id: 'units',
                    label: t('Units'),
                    valueKey: 'units',
                    format: 'number',
                    align: 'right',
                },
                orders: {
                    id: 'orders',
                    label: t('Orders'),
                    valueKey: 'orderCount',
                    format: 'number',
                    align: 'right',
                },
                totalSaved: {
                    id: 'totalSaved',
                    label: t('savingsSummary.totalSavings'),
                    valueKey: 'totalSaved',
                    format: 'currency',
                    align: 'right',
                    highlightColor: '#16a34a',
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('savingsSummary.totalSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                savedPerUnit: {
                    id: 'savedPerUnit',
                    label: t('savingsSummary.propertiesColumnSavedPerUnit'),
                    valueKey: 'savedPerUnit',
                    format: 'currency',
                    align: 'right',
                    highlightColor: '#16a34a',
                },
                savedPerUnitMo: {
                    id: 'savedPerUnitMo',
                    label: t('savingsSummary.propertiesColumnSavedPerUnitMo'),
                    valueKey: 'savedPerUnitMo',
                    format: 'currency',
                    align: 'right',
                    highlightColor: '#16a34a',
                },
            };

            const defaultOrder: SavingsSummaryPropertiesTableColumnId[] = [
                'property',
                'productSourcing',
                'units',
                'orders',
                'totalSaved',
                'savedPerUnit',
                'savedPerUnitMo',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds]);

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const savingsNum = toNumber(item.primaryValue);
                const savedPerUnitNum = toNumber(
                    item.secondaryValues?.savedPerUnit,
                );

                const rows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 8,
                    },
                    buildSavingsRow({
                        id: 'savings',
                        label: t('Savings'),
                        amount: savingsNum,
                        total: args.total,
                        valueFormatter: args.valueFormatter,
                    }),
                ];

                if (savedPerUnitNum > 0) {
                    rows.push({
                        id: 'savedPerUnit',
                        variant: 'labelValue',
                        label: t('savingsSummary.propertiesColumnSavedPerUnit'),
                        value: args.valueFormatter(savedPerUnitNum),
                        positiveValue: true,
                    });
                }

                return rows;
            };

            return {
                widgetId: 'savings-summary-properties',
                title: t('savingsSummary.propertiesSectionTitle'),
                description: subheader,
                layout: tableOnly ? 'stacked' : 'sideBySide',
                visualization: {
                    enabled: !tableOnly,
                    type: 'donut',
                    options: {
                        palette: 'positive',
                        showLegend: false,
                        valueFormatter: (v) =>
                            new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(
                                typeof v === 'number'
                                    ? v
                                    : Number.parseFloat(String(v)) || 0,
                            ),
                        tooltipRowsBuilder,
                    },
                },
                pagination: {
                    mode: 'client',
                    pageSize: TABLE_INITIAL_LIMIT,
                    revealAllOnLoadMore: true,
                },
                table: {
                    enabled: true,
                    columns: tableColumns,
                    options: {
                        showColorSwatchInLabelColumn: 'label',
                        loadMoreVariant: 'link',
                        loadMoreLabel: t('savingsSummary.propertiesShowMore'),
                    },
                },
                onRowClick: handleInteraction,
                onChartItemClick: tableOnly ? undefined : handleInteraction,
            };
        }, [t, handleInteraction, tableOnly, subheader, tableColumns]);

        return (
            <Box
                data-testclass="savings-summary-properties-section"
                data-pdf-section="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    config={config}
                    items={items}
                    chartItems={chartItems}
                    loading={loading}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SavingsSummaryPropertiesSection.displayName = 'SavingsSummaryPropertiesSection';
